/**
 * NodeAssetStrip -- Image overlay showing reference assets and variant count
 *
 * Bottom left: Reference asset thumbnails (characters, locations, uploads)
 * Bottom right: Variants/history button (always visible, shows count)
 *
 * Designed to be positioned absolutely within an image container.
 */

import { memo, useMemo } from 'react'
import { getThumbnailUrl } from '@ponpon/api-client'
import { cn } from '../../../../lib/utils'
import { useProjectId } from '../../../../contexts/ProjectIdContext'

interface NodeAssetStripProps {
  variantCount?: number
  selectedVariantIndex?: number
  onVariantsClick?: () => void
  isVariantsOpen?: boolean
  referenceImages?: string[]
  cacheKey?: string
  isVisible?: boolean
  alwaysShowVariants?: boolean
}

const ReferenceThumbnail = memo(function ReferenceThumbnail({
  src,
  alt,
}: {
  src: string
  alt: string
}) {
  return (
    <div
      className={cn(
        'relative w-10 h-10 rounded-xl overflow-hidden flex-shrink-0',
        'border-2 border-white',
        'shadow-lg'
      )}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </div>
  )
})

ReferenceThumbnail.displayName = 'ReferenceThumbnail'

export const NodeAssetStrip = memo(function NodeAssetStrip({
  variantCount = 0,
  selectedVariantIndex = 0,
  onVariantsClick,
  isVariantsOpen = false,
  referenceImages = [],
  cacheKey,
  isVisible = true,
  alwaysShowVariants = true,
}: NodeAssetStripProps) {
  const projectId = useProjectId()
  const hasReferences = referenceImages.length > 0
  const showVariants = alwaysShowVariants || variantCount > 0

  const referenceThumbnails = useMemo(() => {
    if (!projectId) return []
    return referenceImages
      .map((path, i) => ({
        path,
        url: getThumbnailUrl(projectId, path, 80, cacheKey),
        index: i,
      }))
      .filter(
        (r): r is { path: string; url: string; index: number } =>
          r.url !== null
      )
  }, [projectId, referenceImages, cacheKey])

  if (!hasReferences && !showVariants) {
    return null
  }

  return (
    <>
      {/* Bottom left: Reference asset thumbnails */}
      {hasReferences && (
        <div
          className={cn(
            'absolute bottom-2 left-2 z-10 flex items-center gap-1.5 transition-opacity',
            isVisible ? 'opacity-100' : 'opacity-0'
          )}
        >
          {referenceThumbnails.slice(0, 3).map((r) => (
            <ReferenceThumbnail
              key={r.index}
              src={r.url}
              alt={`Reference ${r.index + 1}`}
            />
          ))}
          {referenceImages.length > 3 && (
            <div className="w-10 h-10 rounded-xl bg-black/70 border-2 border-white flex items-center justify-center backdrop-blur-sm">
              <span className="text-xs text-white font-medium">
                +{referenceImages.length - 3}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Bottom right: Variants/history button */}
      {showVariants && (
        <div
          className={cn(
            'absolute bottom-2 right-2 z-10 transition-opacity',
            isVariantsOpen
              ? 'opacity-100'
              : isVisible
                ? 'opacity-100'
                : 'opacity-0'
          )}
        >
          <button
            onClick={(e) => {
              e.stopPropagation()
              onVariantsClick?.()
            }}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-all nodrag',
              'backdrop-blur-sm',
              isVariantsOpen
                ? 'bg-primary/90 text-primary-foreground'
                : 'bg-black/60 hover:bg-black/80 text-white'
            )}
          >
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <span>{variantCount}</span>
            {variantCount > 0 && (
              <span className="text-text-muted">
                · v{selectedVariantIndex + 1}
              </span>
            )}
          </button>
        </div>
      )}
    </>
  )
})

NodeAssetStrip.displayName = 'NodeAssetStrip'
